import { Component } from '@theme/component';

/**
 * @typedef {object} WishlistButtonRefs
 * @property {HTMLButtonElement} button - The toggle button element.
 * @property {HTMLElement} icon - The SVG icon wrapper.
 * @property {HTMLElement} liveRegion - Screen reader live region.
 */

/**
 * Wishlist toggle button that renders a heart icon with filled/outline states.
 * Communicates with `window.apa.wishlist` for backend operations.
 *
 * @extends {Component<WishlistButtonRefs>}
 */
class WishlistButton extends Component {
  static observedAttributes = ['data-product-id'];

  requiredRefs = ['button', 'icon', 'liveRegion'];

  #inWishlist = false;
  #abortController = new AbortController();
  #isToggling = false;

  connectedCallback() {
    super.connectedCallback();

    const productId = this.dataset.productId;
    if (!productId || !window.apa?.wishlist) return;

    this.#refreshFromProductId(productId);

    document.addEventListener('apa:wishlist:count', this.#onCountSync, {
      signal: this.#abortController.signal,
    });
  }

  /**
   * Re-runs the wishlist state check when the host swaps `data-product-id`
   * (combined-listing product flips inside a quick-add surface).
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-product-id' || oldValue === newValue || !newValue) return;
    if (!this.isConnected || !window.apa?.wishlist) return;
    if (!this.refs.button) return;

    this.#inWishlist = false;
    this.#updateState();
    this.#refreshFromProductId(newValue);
  }

  /**
   * Looks up the wishlist state for a product ID and updates the button. Guards
   * against stale callbacks resolving after a subsequent product change.
   * @param {string} productId
   */
  #refreshFromProductId(productId) {
    window.apa.wishlist.scheduleCheck(productId, (inWishlist) => {
      if (this.#abortController.signal.aborted) return;
      if (this.dataset.productId !== productId) return;
      this.#inWishlist = inWishlist;
      this.#updateState();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles the toggle action (called via on:click="/toggle").
   */
  async toggle() {
    const productId = this.dataset.productId;
    if (!productId || !window.apa?.wishlist || this.refs.button.disabled) return;

    this.refs.button.disabled = true;

    // Optimistic UI
    this.#inWishlist = !this.#inWishlist;
    this.#updateState();
    this.#announce(this.#inWishlist ? this.dataset.announcementAdded : this.dataset.announcementRemoved);

    if (!this.#inWishlist) {
      this.#handleRemove(productId);
      return;
    }

    await this.#handleAdd(productId);
  }

  /**
   * Removal — the card animation stays optimistic (`apa:wishlist:removed` fires immediately),
   * but the `apa:wishlist:count` dispatch waits for the API. That event drives analytics
   * (GA4 `remove_from_wishlist`, Voyado), so firing it optimistically and then dispatching a
   * compensating `inWishlist: true` on failure would record a remove plus an add that never
   * happened. On failure the count event carries no `productId`, which resyncs the wishlist
   * section (restoring the card) without any listener treating it as a toggle.
   * @param {string} productId
   */
  #handleRemove(productId) {
    document.dispatchEvent(new CustomEvent('apa:wishlist:removed', { detail: { productId } }));
    this.refs.button.disabled = false;

    window.apa.wishlist
      .toggle(productId)
      .then(() => {
        if (this.#abortController.signal.aborted) return;

        window.apa.wishlist.count = Math.max(0, (window.apa.wishlist.count || 0) - 1);

        this.#isToggling = true;
        document.dispatchEvent(
          new CustomEvent('apa:wishlist:count', {
            detail: { count: window.apa.wishlist.count, productId, inWishlist: false },
          }),
        );
        this.#isToggling = false;
      })
      .catch(() => {
        if (this.#abortController.signal.aborted) return;

        // Rollback the button state; the section card was animated out optimistically
        this.#inWishlist = true;
        this.#updateState();

        document.dispatchEvent(
          new CustomEvent('apa:wishlist:count', {
            detail: { count: window.apa.wishlist.count || 0 },
          }),
        );
      });
  }

  /**
   * Addition — awaits the API response before dispatching the count event.
   * @param {string} productId
   */
  async #handleAdd(productId) {
    try {
      const { action } = await window.apa.wishlist.toggle(productId);
      if (this.#abortController.signal.aborted) return;

      this.#inWishlist = action === 'added';
      this.#updateState();

      const delta = action === 'added' ? 1 : -1;
      window.apa.wishlist.count = (window.apa.wishlist.count || 0) + delta;

      this.#isToggling = true;
      document.dispatchEvent(
        new CustomEvent('apa:wishlist:count', {
          detail: { count: window.apa.wishlist.count, productId, inWishlist: this.#inWishlist },
        }),
      );
      this.#isToggling = false;
    } catch {
      if (this.#abortController.signal.aborted) return;

      this.#inWishlist = !this.#inWishlist;
      this.#updateState();
    } finally {
      if (!this.#abortController.signal.aborted) {
        this.refs.button.disabled = false;
      }
    }
  }

  /** Syncs state when another button for the same product is toggled. */
  #onCountSync = (event) => {
    if (this.#isToggling) return;
    const { productId, inWishlist } = event.detail ?? {};
    if (productId && String(productId) === this.dataset.productId && inWishlist !== undefined) {
      this.#inWishlist = inWishlist;
      this.#updateState();
    }
  };

  #updateState() {
    const label = this.#inWishlist ? this.dataset.labelRemove : this.dataset.labelAdd;

    this.refs.button.setAttribute('aria-pressed', String(this.#inWishlist));
    this.refs.button.setAttribute('aria-label', label);
    this.refs.icon.dataset.active = String(this.#inWishlist);
  }

  /** @param {string} [text] */
  #announce(text) {
    if (!text) return;
    this.refs.liveRegion.textContent = text;
    setTimeout(() => {
      if (!this.#abortController.signal.aborted) {
        this.refs.liveRegion.textContent = '';
      }
    }, 3000);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
